import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { discoverAPI } from '../services/api';
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 24;

const DiscoverGrid = ({
  filters,
  onPostClick,
  viewedPosts = [],
  searchResults = null // Optional: pre-fetched search results
}) => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);

  const offsetRef = useRef(0);

  const activeFilters = useMemo(() => ({
    instance: filters?.instance ?? 'all',
    slideType: filters?.slideType ?? 'all',
    language: filters?.language ?? 'all',
    difficulty: filters?.difficulty ?? 'all',
  }), [filters]);

  const computedFilters = useMemo(() => ({
    instances: activeFilters.instance === 'all' ? [] : [activeFilters.instance],
    slideTypes: activeFilters.slideType === 'all' ? [] : [activeFilters.slideType],
    languages: activeFilters.language === 'all' ? [] : [activeFilters.language],
    difficulties: activeFilters.difficulty === 'all' ? [] : [activeFilters.difficulty],
  }), [activeFilters]);

  const mapResultToPost = useCallback((result) => {
    const metadata = result?.metadata || {};

    // Parse the content if it's a JSON string (carousel object or slides array)
    let parsedContent = null;
    let contentTitle = null;
    let contentBody = null;
    let slides = null;

    if (result.content) {
      if (typeof result.content === 'string') {
        const contentString = result.content.trim();
        try {
          // Try to parse as JSON
          if (contentString.startsWith('{')) {
            // Carousel object: {"id":"carousel_...", "title":"...", "slides":[...]}
            parsedContent = JSON.parse(contentString);
            contentTitle = parsedContent.title;
            contentBody = parsedContent.slides?.[0]?.content || parsedContent.slides?.[0]?.title;
            slides = parsedContent.slides;
          } else if (contentString.startsWith('[')) {
            // Slides array: [{"type":"concept", "title":"...", "content":"..."}]
            const slidesArray = JSON.parse(contentString);
            if (Array.isArray(slidesArray) && slidesArray.length > 0) {
              contentTitle = slidesArray[0].title;
              contentBody = slidesArray[0].content || slidesArray[0].title;
              slides = slidesArray;
            }
          } else {
            // Plain text content
            contentBody = contentString;
          }
        } catch (e) {
          // If parsing fails, use as plain text
          contentBody = contentString;
        }
      } else if (Array.isArray(result.content)) {
        const slidesArray = result.content;
        if (Array.isArray(slidesArray) && slidesArray.length > 0) {
          contentTitle = slidesArray[0].title;
          contentBody = slidesArray[0].content || slidesArray[0].title;
          slides = slidesArray;
        }
      } else if (typeof result.content === 'object') {
        parsedContent = result.content;
        contentTitle = parsedContent?.title;
        const firstSlide = parsedContent?.slides?.[0];
        contentBody = firstSlide?.content || firstSlide?.title;
        slides = parsedContent?.slides;
      }
    }

    const titleFromMetadata = metadata.slide_title || metadata.title;
    const fallbackTitle = contentTitle || (contentBody ? contentBody.substring(0, 60) : 'Discover content');

    return {
      id: result.id,
      title: titleFromMetadata || contentTitle || fallbackTitle,
      body: contentBody || result.content,
      content: contentBody || result.content,
      media_url: metadata.media_url,
      type: metadata.slide_type || 'post',
      instance_id: metadata.instance_id,
      document_id: metadata.document_id,
      language: metadata.language,
      difficulty: metadata.difficulty,
      source_ref: metadata.source_ref,
      source_line_start: metadata.line_start ?? metadata.source_line_start,
      source_line_end: metadata.line_end ?? metadata.source_line_end,
      metadata,
      sourceAttribution: result.sourceAttribution,
      source: result.source,
      likes: metadata.likes || 0,
      comments: metadata.comments || 0,
      slides: slides, // Include slides if available
    };
  }, []);

  const fetchDiscoverPosts = useCallback(async (reset = false) => {
    // If searchResults are provided, use them instead of fetching
    if (searchResults !== null) {
      setLoading(false);
      setPosts(searchResults);
      setHasMore(false);
      return;
    }

    const currentOffset = reset ? 0 : offsetRef.current;
    setLoading(true);

    try {
      const response = await discoverAPI.search({
        query: '',
        limit: PAGE_SIZE,
        offset: currentOffset,
        instances: computedFilters.instances,
        slideTypes: computedFilters.slideTypes,
        languages: computedFilters.languages,
        difficulties: computedFilters.difficulties,
      });

      const results = (response?.results || []).map(mapResultToPost);
      offsetRef.current = currentOffset + results.length;
      setHasMore(Boolean(response?.metadata?.hasMore) || results.length === PAGE_SIZE);

      setPosts(prev => {
        const base = reset ? [] : [...prev];
        const existingIds = new Set(base.map(item => item.id));

        results.forEach(item => {
          if (!existingIds.has(item.id)) {
            base.push(item);
            existingIds.add(item.id);
          }
        });

        return base;
      });
    } catch (error) {
      console.error('Error fetching discover posts:', error);
      if (reset) {
        setPosts([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  }, [computedFilters, mapResultToPost, searchResults]);

  useEffect(() => {
    offsetRef.current = 0;
    setHasMore(true);
    fetchDiscoverPosts(true);
  }, [fetchDiscoverPosts]);

  const loadMorePosts = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchDiscoverPosts(false);
  }, [fetchDiscoverPosts, hasMore, loading]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 800
      ) {
        loadMorePosts();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMorePosts]);

  if (loading && posts.length === 0) {
    return (
      <div className="grid grid-cols-3 gap-1">
        {[...Array(9)].map((_, index) => (
          <div
            key={index}
            className="aspect-square bg-gray-200 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (posts.length === 0 && !loading) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-200 rounded-lg mx-auto mb-4" />
        <h3 className="font-semibold text-gray-900 mb-2">{t('search.no_results')}</h3>
        <p className="text-gray-600 text-sm">
          {t('search.adjust_filters')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {posts.length === 0 && loading ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-200 rounded-lg mx-auto mb-4" />
          <p className="text-gray-600 text-sm">{t('search.loading')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-1">
            {posts.map((post, index) => {
              const isViewed = viewedPosts.includes(post.id);

              return (
                <div
                  key={post.id}
                  className="aspect-square bg-gray-100 relative cursor-pointer hover:opacity-90 transition-opacity group"
                  onClick={() => onPostClick(post, posts)}
                >
                  {post.media_url ? (
                    <img
                      src={post.media_url}
                      alt={post.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                      <span className="text-xs text-gray-700 text-center px-2 font-medium">
                        {(post.title || t('search.placeholder')).substring(0, 40)}
                      </span>
                    </div>
                  )}

                  {isViewed && (
                    <div className="absolute top-1 right-1 w-3 h-3 bg-green-500 rounded-full border border-white" />
                  )}

                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                    <div className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity text-center">
                      <div className="flex items-center justify-center gap-3 mb-1">
                        <span className="flex items-center gap-1">{t('carousel.likes_count', { count: post.likes || 0 })}</span>
                        <span className="flex items-center gap-1">{post.comments || 0} {t('common.done')}</span>
                      </div>
                      <div className="text-xs text-white text-opacity-80">
                        {(post.language ? post.language.toUpperCase() : t('search.multi_lang'))} · {post.type}
                      </div>
                    </div>
                  </div>

                  {!isViewed && index < 3 && (
                    <div className="absolute top-1 left-1 w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                  )}
                </div>
              );
            })}

            {loading && (
              [...Array(6)].map((_, index) => (
                <div
                  key={`loading-${index}`}
                  className="aspect-square bg-gray-200 animate-pulse"
                />
              ))
            )}
        </div>
      )}

      <div className="text-center py-4">
        {hasMore ? (
          <div className="text-xs text-gray-500">
            {t('search.scroll_more')}
          </div>
        ) : (
          <div className="text-xs text-gray-400">
            {t('search.caught_up')}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscoverGrid;
